import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";
import BoiteOrga from "./BoiteOrga";

/**
 * Page d'arrivée depuis le logiciel métier IDM Optic.
 * Accessible uniquement depuis l'encart IDM Optic — non référencée (noindex).
 * Volontairement hors PublicShell : aucune navigation MetaConnect,
 * le professionnel ne doit pas sortir du parcours de souscription.
 */

const CONNEXION_URL = "https://ohmyvue.fr/login";

const POINTS = [
  {
    t: "Vous ouvrez sur une journée déjà organisée",
    d: "Vos créneaux sont publiés sur votre page. Le patient choisit sa prestation, son praticien et son horaire — un dimanche soir s'il le veut. Vous n'avez plus qu'à préparer les dossiers de la journée.",
    li: [
      "Réservation ouverte 24 h/24, y compris hors horaires",
      "Confirmation puis rappel envoyés au patient, sans vous",
      "Un agenda par professionnel rattaché, sans supplément",
      "Le nombre de rendez-vous à venir s'affiche dans IDM Optic ou IDM Audio",
    ],
    link: { to: "/agenda-type", label: "Voir un agenda type, opticien et audio" },
    before: "Aujourd'hui : le patient qui appelle à 20 h tombe sur un répondeur.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 11h18" />
        <path d="M8.5 15.5l2 2 4-4" />
      </svg>
    ),
  },
  {
    t: "Le patient arrive avec son dossier déjà prêt",
    d: "Plus aucun document sur une boîte Gmail. Depuis la page de votre établissement, le patient téléverse lui-même ce dont vous avez besoin : ordonnance, carte de mutuelle, attestation de Sécurité sociale, certificats d'authenticité verres, monture ou lentilles. Tout se range dans son rendez-vous, dans OhMyVue, avant qu'il pousse la porte.",
    li: [
      "Un dépôt sécurisé depuis votre page, pas une boîte grand public",
      "Ordonnance, mutuelle, attestation, certificats d'authenticité",
      "Identité vérifiée : le document ne se trompe pas de patient",
      "Vous le relisez la veille, depuis le rendez-vous, sans scan ni renommage",
    ],
    link: {
      to: "/dossier-patient",
      label: "Voir ce que le patient prépare avant de venir",
    },
    before: "Aujourd'hui : une photo par SMS, une pièce jointe sur Gmail, un scan à renommer.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14 3v5h5" />
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M12 17.5v-6M9.5 14l2.5-2.5L14.5 14" />
      </svg>
    ),
  },
  {
    t: "Partager avec l'ensemble des professionnels de santé n'a jamais été aussi simple",
    d: "Ophtalmologiste, orthoptiste, médecin traitant, hôpital, clinique, confrère opticien ou audioprothésiste : le même geste pour tous. OhMyVue trouve le correspondant dans l'annuaire santé, met en forme le courrier et l'envoie depuis la boîte de votre établissement. Il arrive dans la boîte sécurisée du destinataire, structuré pour son logiciel métier.",
    li: [
      "Le correspondant se trouve dans l'annuaire santé, sans le chercher",
      "Un format structuré, repris directement dans leur logiciel métier",
      "Une preuve d'envoi horodatée, conservée dans OhMyVue",
      "Le compteur de messages en attente s'affiche dans IDM Optic ou IDM Audio",
    ],
    link: {
      to: "/idm-optic/message-type",
      label: "Voir le message envoyé à l'ophtalmologiste ou à l'orthoptiste",
    },
    before: "Aujourd'hui : un fax, un courrier, ou un mail dont on ignore le sort.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 4L3 11l7 3 3 7z" />
        <path d="M21 4l-11 10" />
      </svg>
    ),
  },
  {
    t: "Le dossier médical partagé, en consultation et en alimentation",
    d: "Vous accédez à Mon espace santé et au dossier médical partagé dans les deux sens : vous consultez ce que les autres professionnels y ont déposé, et vous l'alimentez à votre tour. C'est ce qui rend le suivi des soins continu et la coordination entre les 3 O — ophtalmologiste, orthoptiste, opticien — enfin réelle.",
    li: [
      "Consultation : dernière ordonnance, comptes rendus, historique",
      "Alimentation : vos adaptations déposées au moment de l'envoi",
      "Le patient retrouve ses documents dans Mon espace santé",
      "Une trace horodatée de ce que vous avez fait, et quand",
    ],
    link: {
      to: "/idm-optic/dmp",
      label: "Voir comment on consulte et on alimente le dossier",
    },
    before: "Aujourd'hui : chacun garde ses documents dans son coin.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 21s7.5-4.2 7.5-10V5.5L12 2.7 4.5 5.5V11c0 5.8 7.5 10 7.5 10z" />
        <path d="M9.2 12l2 2 3.6-3.8" />
      </svg>
    ),
  },
];

const STEPS = [
  {
    n: "01",
    t: "Vous vous connectez",
    d: "Carte CPS ou application e-CPS. Cette seule action vous identifie et identifie votre établissement. Aucun mot de passe.",
  },
  {
    n: "02",
    t: "Vous choisissez votre offre",
    d: "Établissement déjà renseigné, signature électronique, paiement par carte. Contrat et facture suivent.",
  },
  {
    n: "03",
    t: "Nous ouvrons votre boîte",
    d: "Vous déclarez vos professionnels, nous créons la messagerie. Le raccourci dans IDM Optic ou IDM Audio se met à afficher vos compteurs.",
  },
];

const OFFERS = [
  {
    name: "Messagerie de Santé organisationnelle",
    price: "19,99 €",
    featured: false,
    items: [
      "Votre messagerie sécurisée de santé",
      "Tous vos professionnels rattachés, sans supplément",
      "Envois et réceptions illimités, traçabilité horodatée",
      "Visibilité dans l'annuaire santé",
      "Site vitrine personnalisable inclus",
      "Raccourci et compteurs dans IDM Optic ou IDM Audio, inclus",
    ],
  },
  {
    name: "Messagerie + Agenda",
    price: "49,99 €",
    featured: true,
    items: [
      "Tout ce que contient l'offre précédente",
      "Agenda connecté, interopérable avec vos activités",
      "Prise de rendez-vous en ligne",
      "Un agenda par professionnel rattaché",
      "Rappels automatiques aux patients",
    ],
  },
  {
    name: "Messagerie + Agenda + Suivi myopie",
    logo: "/logos/myo-control.jpeg",
    soon: true,
    price: "79,99 €",
    featured: false,
    items: [
      "Tout ce que contient l'offre précédente",
      "Module Myo-Control pour la freination myopique",
      "Passeports et carnets digitalisés",
      "Document unique alimentant le DMP",
    ],
  },
];

const SOCLES = [
  { src: "/logos/insi.png", alt: "INSi — Identité Nationale de Santé" },
  { src: "/logos/mssante-official.png", alt: "MSSanté" },
  { src: "/logos/dmp.png", alt: "Dossier Médical Partagé" },
  { src: "/logos/mon-espace-sante.png", alt: "Mon espace santé" },
  { src: "/logos/psc.png", alt: "Pro Santé Connect" },
  { src: "/logos/hds.png", alt: "Hébergeur de données de santé certifié" },
];

const MORE = [
  { t: "L'agenda et la prise de rendez-vous en ligne", to: "/agenda" },
  { t: "Le site vitrine inclus dans l'offre", to: "/agenda#vitrine" },
  { t: "La MSSanté pour les opticiens", to: "/mssante/opticien" },
  { t: "La MSSanté pour les audioprothésistes", to: "/mssante/audioprothesiste" },
  { t: "L'identité nationale de santé", to: "/services/ins" },
  { t: "Le dossier médical partagé", to: "/services/dmp" },
  { t: "Pro Santé Connect et la carte CPS", to: "/services/psc" },
  { t: "Conformité et hébergement", to: "/conformite" },
];

const Tuile = () => (
  <div className="pop-tuile" aria-label="Le raccourci OhMyVue dans le tableau de bord IDM Optic ou IDM Audio">
    <div className="pop-tu-hd">
      <img src="/logos/ohmyvue.png" alt="OhMyVue" />
      <span className="pop-tu-go" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </span>
    </div>
    <div className="pop-tu-ct">
      <div>
        <em className="pop-tu-svc">Agenda</em>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M8 3v4M16 3v4M3 11h18" />
        </svg>
        <b>4</b>
        <span>rendez-vous à venir</span>
      </div>
      <div>
        <em className="pop-tu-svc">Messagerie de santé</em>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3.5 7l8.5 6 8.5-6" />
        </svg>
        <b>2</b>
        <span>messages en attente</span>
      </div>
    </div>
    <div className="pop-tu-next">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7.5V12l3 1.8" />
      </svg>
      <b>14:30</b>
      <span>Mme RIVIÈRE — contrôle de vue</span>
    </div>
  </div>
);

const Arrow = () => (
  <svg className="pop-arw" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const Tick = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);

export default function ProOptic() {
  return (
    <div className="pop-osm">
      <SEO
        title="IDM Optic et IDM Audio × OhMyVue — vos fonctionnalités e-santé"
        description="Page réservée aux opticiens et audioprothésistes équipés d'IDM Optic ou d'IDM Audio : messagerie sécurisée de santé, agenda connecté et prise de rendez-vous en ligne, espace patient, dossier médical partagé — activés avec votre carte CPS."
        canonical="/idm-optic"
        noIndex
      />

      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&display=swap"
        />
      </Helmet>

      <style>{CSS}</style>

      {/* ---------------- barre haute ---------------- */}
      <header className="pop-topbar">
        <div className="pop-wrap">
          <img className="pop-mark" src="/logos/ohmyvue.png" alt="OhMyVue" />
          <span className="pop-spacer" />
          <a className="pop-btn pop-btn-primary pop-btn-sm" href={CONNEXION_URL}>
            Connexion
          </a>
        </div>
      </header>

      {/* ---------------- hero : le partenariat ---------------- */}
      <div className="pop-hero">
        <div className="pop-wrap">
          <div className="pop-lockup">
            <img className="pop-osmose" src="/logos/prooptic.png" alt="IDM Optic" />
            <span className="pop-x" aria-hidden="true">
              ×
            </span>
            <img className="pop-omv" src="/logos/ohmyvue.png" alt="OhMyVue" />
            <div className="pop-tag">Partenariat technologique</div>
          </div>

          <h1>
            Toutes vos fonctionnalités e-santé, <em>ouvertes depuis IDM Optic et IDM Audio.</em>
          </h1>
          <p className="pop-lede">
            <b>Messagerie sécurisée de santé</b>, <b>agenda connecté et prise de rendez-vous en
            ligne</b>, <b>espace patient pour le dépôt de documents</b>, <b>consultation et
            alimentation du dossier médical partagé</b> : quatre services que vous ouvrez avec votre
            carte CPS. Dans IDM Optic comme dans IDM Audio, un raccourci vous dit ce qui vous attend ; tout le reste se
            passe dans OhMyVue.
          </p>
          <p className="pop-claim">
            Vous n'ajoutez pas un logiciel de plus. Vous entrez dans le réseau où ophtalmologistes,
            orthoptistes, opticiens et audioprothésistes se répondent enfin.
          </p>

          <div className="pop-actions">
            <a className="pop-btn pop-btn-primary pop-btn-lg" href={CONNEXION_URL}>
              Connexion <Arrow />
            </a>
            <a className="pop-btn pop-btn-ghost-dark pop-btn-lg" href="#offres">
              Voir les offres
            </a>
          </div>
        </div>
      </div>

      {/* ---------------- bande services socles ---------------- */}
      <div className="pop-strip">
        <div className="pop-wrap">
          <p className="pop-strip-lead">
            OhMyVue est raccordé aux services socles de l'État. L'identité est qualifiée, le message
            transmis, le document déposé.
          </p>
          <div className="pop-marks">
            {SOCLES.map((s) => (
              <img key={s.src} src={s.src} alt={s.alt} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* ---------------- ce qu'IDM Optic affiche ---------------- */}
      <section className="pop-sec pop-int">
        <div className="pop-wrap">
          <div className="pop-head">
            <span className="pop-pill pop-pill-light">L'intégration</span>
            <h2>Ce qu'IDM Optic et IDM Audio affichent</h2>
            <p>
              Une tuile dans votre tableau de bord : le nombre de rendez-vous à venir, le nombre de
              messages en attente dans votre messagerie de santé, et l'heure du prochain rendez-vous.
              Un clic ouvre OhMyVue.
            </p>
          </div>
          <Tuile />
          <p className="pop-int-note">
            C'est tout ce qui circule entre les deux logiciels : deux compteurs et une ligne. IDM Optic et IDM Audio
            gardent la vente, le stock et la facturation ; OhMyVue porte l'identité de santé et tout ce
            qui en dépend. Il n'y a rien à synchroniser, rien à paramétrer des deux côtés.
          </p>
        </div>
      </section>

      {/* ---------------- la boîte de l'établissement ---------------- */}
      <section className="pop-sec">
        <div className="pop-wrap">
          <div className="pop-head">
            <span className="pop-pill pop-pill-light">Messagerie sécurisée de santé</span>
            <h2>Une seule boîte, pour tout l'établissement</h2>
            <p>
              Une adresse qui appartient au magasin, pas à une personne. Vos professionnels y sont
              rattachés, tout le monde voit le même flux, et chaque envoi porte le nom de celui qui
              l'a envoyé.
            </p>
          </div>
          <BoiteOrga />
        </div>
      </section>

      {/* ---------------- trois bénéfices ---------------- */}
      <section className="pop-sec">
        <div className="pop-wrap">
          <div className="pop-head">
            <span className="pop-pill pop-pill-light">Ce que ça change</span>
            <h2>Quatre choses qui changent votre journée</h2>
            <p>
              Vous gardez IDM Optic ou IDM Audio pour la vente, le stock et la facturation. OhMyVue prend ce qui
              relève de la santé — et vous prévient dans votre logiciel métier quand quelque chose vous attend.
            </p>
          </div>
          <div className="pop-four">
            {POINTS.map((p) => (
              <div className="pop-pt" key={p.t}>
                <div className="pop-ic">{p.icon}</div>
                <h3>{p.t}</h3>
                <p>{p.d}</p>
                <ul>
                  {p.li.map((x) => (
                    <li key={x}>
                      <Tick />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
                {p.link && (
                  <Link className="pop-seelink" to={p.link.to}>
                    {p.link.label} <Arrow />
                  </Link>
                )}
                <span className="pop-before">{p.before}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- démarrer ---------------- */}
      <section className="pop-sec pop-dark">
        <div className="pop-wrap">
          <div className="pop-head">
            <span className="pop-pill pop-pill-dark">Démarrer</span>
            <h2>Trois étapes, votre carte CPS suffit</h2>
          </div>
          <ol className="pop-flow">
            {STEPS.map((s) => (
              <li className="pop-fl" key={s.n}>
                <div className="pop-n">{s.n}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </li>
            ))}
          </ol>
          <p className="pop-flow-note">
            Activation sous 48 heures ouvrées. Vos professionnels sont rattachés sans limite de nombre
            ni supplément.
          </p>
        </div>
      </section>

      {/* ---------------- offres ---------------- */}
      <section className="pop-sec" id="offres">
        <div className="pop-wrap">
          <div className="pop-head">
            <span className="pop-pill pop-pill-light">Tarifs</span>
            <h2>Un prix par établissement</h2>
          </div>
          <div className="pop-offers">
            {OFFERS.map((o) => (
              <div className={"pop-offer" + (o.featured ? " pop-feat" : "") + (o.soon ? " pop-bientot" : "")} key={o.name}>
                {o.featured && <span className="pop-badge">La plus complète</span>}
                {o.soon && <span className="pop-badge pop-badge-soon">Bientôt disponible</span>}
                <div className="pop-oname">{o.name}</div>
                <div className="pop-price">
                  <b>{o.price}</b>
                  <span>/ mois HT</span>
                </div>
                <div className="pop-osub">Par établissement, engagement 12 mois</div>
                {o.logo && (<div className="pop-omodule"><img src={o.logo} alt="Module Myo-Control" /></div>)}
                <ul>
                  {o.items.map((it) => (
                    <li key={it}>
                      <Tick />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                {o.soon && (<div className="pop-onote">Module en cours de finalisation. Activation possible depuis votre espace OhMyVue dès son ouverture.</div>)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- le choix se fait après la connexion ---------------- */}
      <section className="pop-confirm">
        <div className="pop-wrap">
          <div className="pop-cb-txt">
            <div className="pop-cb-k">Important</div>
            <h2>Le choix de votre offre se fait après la connexion.</h2>
            <p>
              Ces encarts vous présentent le contenu de chaque offre. La sélection et le paiement ont
              lieu sur la plateforme OhMyVue, une fois votre établissement identifié par Pro Santé
              Connect — c'est là que vous choisirez, et nulle part ailleurs.
            </p>
          </div>
          <div className="pop-actions">
            <a className="pop-btn pop-btn-white pop-btn-lg" href={CONNEXION_URL}>
              Connexion <Arrow />
            </a>
            <Link className="pop-btn pop-btn-ghostw pop-btn-lg" to="/demo">
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </section>

      
      {/* ---------------- pour aller plus loin ---------------- */}
      <section className="pop-sec pop-more-sec">
        <div className="pop-wrap">
          <div className="pop-head">
            <span className="pop-pill pop-pill-light">Pour aller plus loin</span>
            <h2>Tout comprendre, sur metaconnect.fr</h2>
          </div>
          <div className="pop-more">
            {MORE.map((m) => (
              <Link className="pop-lnk" to={m.to} target="_blank" rel="noopener" key={m.to}>
                <span>
                  <span className="pop-lt">{m.t}</span>
                  <span className="pop-lu">metaconnect.fr{m.to}</span>
                </span>
                <span className="pop-go">
                  <Arrow />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- pied de page ---------------- */}
      <footer className="pop-foot">
        <div className="pop-wrap">
          <div className="pop-op">
            <img src="/logos/ohmyvue.png" alt="OhMyVue" />
            <span>
              Service opéré par MetaConnect.fr, opérateur MSSanté référencé auprès de l'ANS.
            </span>
          </div>
          <div className="pop-links">
            <Link to="/mentions-legales">Mentions légales</Link>
            <Link to="/cgv">CGV</Link>
            <Link to="/cgu">CGU</Link>
            <a href="mailto:contact@metaconnect.fr">contact@metaconnect.fr</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Styles portés par la page. Tout est préfixé .pop-osm : rien ne fuit vers
   le reste du site, et les règles globales sur h1..h6 sont neutralisées.
   Charte reprise de ohmyvue.fr : Instrument Sans, bandes #0F172A / #F5F5F5,
   primaire #0080FF, boutons entièrement arrondis.
   ------------------------------------------------------------------------- */
const CSS = `
.pop-osm{
  --ink:#0F172A; --ink2:#1B2540; --ground:#F5F5F5; --surface:#FFFFFF;
  --line:#E6E8EC; --blue:#0080FF; --blue700:#0064CC; --bsoft:#EFF6FF;
  --bline:#D6E7FB; --text:#0F172A; --muted:#5A6577; --mutedd:#A8B3C7; --ok:#0FA36B;
  background:var(--ground); color:var(--text); min-height:100vh;
  font-family:"Instrument Sans",ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;
  font-size:15.5px; line-height:1.6; -webkit-font-smoothing:antialiased;
}
.pop-osm *{box-sizing:border-box}
.pop-osm img{max-width:100%}
.pop-wrap{max-width:1080px;margin:0 auto;padding:0 28px}
.pop-osm h1,.pop-osm h2,.pop-osm h3{margin:0;text-wrap:balance;color:var(--text);font-weight:600}
.pop-osm h1{font-size:clamp(31px,4vw,44px);line-height:1.12;letter-spacing:-.02em}
.pop-osm h2{font-size:clamp(23px,2.4vw,29px);line-height:1.2;letter-spacing:-.015em}
.pop-osm h3{font-size:16.5px;line-height:1.35}
.pop-osm p{margin:0;color:var(--muted)}
.pop-osm ul,.pop-osm ol{list-style:none;margin:0;padding:0}
.pop-dark{background:var(--ink)}
.pop-dark h2,.pop-dark h3{color:#fff}
.pop-dark p{color:var(--mutedd)}

.pop-btn{display:inline-flex;align-items:center;gap:9px;border-radius:999px;font-weight:600;
  font-size:14px;text-decoration:none;border:1px solid transparent;padding:11px 22px;
  transition:background .15s,border-color .15s;cursor:pointer}
.pop-btn-primary{background:var(--blue);color:#fff}
.pop-btn-primary:hover{background:var(--blue700);color:#fff}
.pop-btn-ghost-dark{border-color:rgba(255,255,255,.24);color:#fff}
.pop-btn-ghost-dark:hover{border-color:rgba(255,255,255,.5);color:#fff}
.pop-btn-white{background:#fff;color:var(--blue);font-size:15.5px;padding:15px 30px}
.pop-btn-white:hover{background:#EAF4FF;color:var(--blue)}
.pop-btn-lg{padding:14px 28px;font-size:15px}
.pop-btn-sm{padding:9px 18px;font-size:13.5px}
.pop-arw{width:15px;height:15px;stroke:currentColor;fill:none;stroke-width:2;
  stroke-linecap:round;stroke-linejoin:round;flex:0 0 15px}

.pop-pill{display:inline-flex;align-items:center;border-radius:999px;font-size:11.5px;
  font-weight:600;letter-spacing:.11em;text-transform:uppercase;padding:7px 15px}
.pop-pill-dark{background:rgba(255,255,255,.09);color:#DCE5F5}
.pop-pill-light{background:#E7ECF3;color:#45526B}

/* barre haute */
.pop-topbar{background:var(--ink)}
.pop-topbar .pop-wrap{display:flex;align-items:center;height:60px}
.pop-mark{height:19px;width:auto;filter:brightness(0) invert(1)}
.pop-spacer{flex:1}

/* hero */
.pop-hero{background:var(--ink);position:relative;overflow:hidden}
.pop-hero::after{content:"";position:absolute;right:-200px;top:-280px;width:720px;height:720px;
  border-radius:50%;pointer-events:none;
  background:radial-gradient(circle,rgba(0,128,255,.18) 0%,rgba(0,128,255,0) 62%)}
.pop-hero .pop-wrap{padding:46px 28px 54px;position:relative;z-index:1}
.pop-lockup{display:flex;align-items:center;gap:32px;flex-wrap:wrap;padding-bottom:30px;
  border-bottom:1px solid rgba(255,255,255,.10);margin-bottom:34px}
.pop-osmose{height:92px;width:auto}
.pop-omv{height:42px;width:auto;filter:brightness(0) invert(1)}
.pop-x{width:34px;height:34px;flex:0 0 34px;border-radius:50%;display:grid;place-items:center;
  border:1px solid rgba(255,255,255,.18);color:rgba(255,255,255,.55);font-size:15px}
.pop-tag{margin-left:auto;font-size:11.5px;font-weight:600;letter-spacing:.11em;
  text-transform:uppercase;color:#5FB3FF}
.pop-hero h1{color:#fff;max-width:17ch;margin-bottom:18px}
.pop-hero h1 em{font-style:normal;color:#5FB3FF}
.pop-lede{max-width:54ch}
.pop-hero .pop-lede{font-size:19px;line-height:1.62;color:#AEC1DC}
.pop-hero .pop-lede b{color:#fff;font-weight:600}
.pop-claim{max-width:52ch;margin-top:18px;font-size:16px;font-weight:600;color:#fff;
  padding-left:16px;border-left:2px solid var(--blue);line-height:1.5}
.pop-actions{display:flex;flex-wrap:wrap;gap:12px;margin-top:28px}

/* bande services socles */
.pop-strip{background:var(--surface);border-bottom:1px solid var(--line);padding:26px 0}
.pop-strip .pop-wrap{display:flex;align-items:center;gap:28px;flex-wrap:wrap}
.pop-strip-lead{font-size:13.4px;color:var(--muted);max-width:34ch;flex:1 1 260px;line-height:1.5}
.pop-marks{display:flex;align-items:center;gap:26px;flex-wrap:wrap}
.pop-marks img{height:32px;width:auto;object-fit:contain;opacity:.92}

.pop-sec{padding:66px 0}
.pop-head{max-width:58ch;margin-bottom:32px}
.pop-head h2{margin-top:14px}

/* trois bénéfices */
.pop-three{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.pop-pt{background:var(--surface);border:1px solid var(--line);border-radius:16px;padding:24px 22px}
.pop-ic{width:38px;height:38px;border-radius:11px;display:grid;place-items:center;margin-bottom:15px;
  background:var(--bsoft);border:1px solid var(--bline)}
.pop-ic svg{width:18px;height:18px;stroke:var(--blue);fill:none;stroke-width:1.9;
  stroke-linecap:round;stroke-linejoin:round}
.pop-pt h3{margin-bottom:8px}
.pop-pt p{font-size:13.8px;line-height:1.55}

/* démarrer */
.pop-flow{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.pop-fl{background:var(--ink2);border:1px solid rgba(255,255,255,.09);border-radius:14px;padding:22px 20px}
.pop-n{font-size:12px;font-weight:600;color:#5FB3FF;letter-spacing:.08em;font-variant-numeric:tabular-nums}
.pop-fl h3{margin:9px 0 7px;font-size:15.5px;color:#fff}
.pop-fl p{font-size:13.6px;line-height:1.55;color:var(--mutedd)}
.pop-flow-note{margin-top:18px;font-size:13.4px;color:#8F9DB4}

/* offres */
.pop-offers{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;align-items:stretch}
.pop-offer{background:var(--surface);border:1px solid var(--line);border-radius:18px;
  padding:26px 24px;display:flex;flex-direction:column;position:relative}
.pop-feat{border-color:var(--blue);box-shadow:0 12px 34px -18px rgba(0,128,255,.55)}
.pop-badge{position:absolute;top:-11px;left:24px;background:var(--blue);color:#fff;border-radius:999px;
  font-size:10.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:5px 13px}
.pop-oname{font-size:15.5px;font-weight:600;line-height:1.3;min-height:2.6em}
.pop-price{display:flex;align-items:baseline;gap:7px;margin-top:14px}
.pop-price b{font-size:33px;font-weight:600;letter-spacing:-.02em;font-variant-numeric:tabular-nums}
.pop-price span{font-size:13.5px;color:var(--muted)}
.pop-osub{font-size:12.4px;color:var(--muted);margin-top:3px}
.pop-omodule{margin-top:16px;padding-top:14px;border-top:1px dashed var(--line)}
.pop-omodule img{height:50px;width:auto;display:block}
.pop-offer ul{margin:20px 0 0;display:grid;gap:9px;flex:1;align-content:start}
.pop-offer li{display:flex;gap:10px;font-size:13.5px;line-height:1.5;color:#3D4759}
.pop-offer li svg{width:14px;height:14px;flex:0 0 14px;margin-top:4px;stroke:var(--ok);fill:none;
  stroke-width:2.6;stroke-linecap:round;stroke-linejoin:round}

/* bandeau : le choix se fait après la connexion */
.pop-confirm{background:var(--blue);padding:52px 0}
.pop-confirm .pop-wrap{display:flex;align-items:center;gap:36px;flex-wrap:wrap;
  justify-content:space-between}
.pop-cb-txt{flex:1 1 480px}
.pop-cb-k{font-size:11.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;
  color:rgba(255,255,255,.72);margin-bottom:12px}
.pop-confirm h2{color:#fff;font-size:clamp(25px,3vw,34px);margin-bottom:14px}
.pop-confirm p{color:rgba(255,255,255,.88);font-size:15.5px;max-width:62ch;line-height:1.6}

/* connexion */
.pop-connect .pop-wrap{display:grid;grid-template-columns:1.05fr .95fr;gap:36px;align-items:center}
.pop-connect h2{margin:16px 0 14px}
.pop-connect p{max-width:50ch}
.pop-sim{background:var(--ink2);border:1px solid rgba(255,255,255,.10);border-radius:18px;padding:26px}
.pop-sim-hd{display:flex;align-items:center;gap:12px;padding-bottom:16px;
  border-bottom:1px solid rgba(255,255,255,.10);margin-bottom:18px}
.pop-sim-hd img{height:17px;width:auto;filter:brightness(0) invert(1)}
.pop-sim-hd span{font-size:12.5px;color:#8F9DB4;margin-left:auto}
.pop-sim-grid{display:grid;gap:15px}
.pop-lab{font-size:11.5px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:#7F8DA5}
.pop-val{font-size:15px;font-weight:600;color:#fff;margin-top:4px}
.pop-sim-psc{margin-top:19px;display:flex;align-items:center;gap:12px;
  background:rgba(0,128,255,.12);border:1px solid rgba(0,128,255,.32);border-radius:12px;padding:14px 16px}
.pop-sim-psc svg{width:19px;height:19px;flex:0 0 19px;stroke:#5FB3FF;fill:none;stroke-width:1.9;
  stroke-linecap:round;stroke-linejoin:round}
.pop-sim-psc div{font-size:13.4px;color:#CFDCEF;line-height:1.45}

/* pour aller plus loin */
.pop-more-sec{background:var(--surface);border-top:1px solid var(--line)}
.pop-more{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.pop-lnk{display:flex;align-items:center;gap:14px;background:var(--ground);
  border:1px solid var(--line);border-radius:13px;padding:16px 18px;text-decoration:none;
  color:var(--text);transition:border-color .15s,transform .15s}
.pop-lnk:hover{border-color:var(--blue);transform:translateY(-1px);color:var(--text)}
.pop-lt{display:block;font-size:14.2px;font-weight:600;line-height:1.3}
.pop-lu{display:block;font-size:12.4px;color:var(--muted);margin-top:2px}
.pop-go{margin-left:auto;color:var(--blue);display:grid;place-items:center;flex:0 0 auto}

/* pied de page */
.pop-foot{background:#0A1120;color:#8C99AD;padding:34px 0;font-size:13px}
.pop-foot .pop-wrap{display:flex;flex-wrap:wrap;gap:18px;align-items:center;
  justify-content:space-between}
.pop-op{display:flex;align-items:center;gap:11px;max-width:58ch}
.pop-op img{height:17px;width:auto;opacity:.75;filter:brightness(0) invert(1)}
.pop-links{display:flex;gap:18px;flex-wrap:wrap}
.pop-foot a{color:#B7C3D8;text-decoration:none}
.pop-foot a:hover{text-decoration:underline;color:#fff}

@media (max-width:900px){
  .pop-three,.pop-flow,.pop-offers,.pop-more{grid-template-columns:1fr}
  .pop-connect .pop-wrap{grid-template-columns:1fr}
  .pop-osmose{height:66px}
  .pop-tag{margin-left:0;width:100%}
  .pop-marks{gap:20px}
  .pop-marks img{height:26px}
}
@media (prefers-reduced-motion:reduce){.pop-osm *{transition:none !important}}
.pop-osm a:focus-visible,.pop-osm button:focus-visible{outline:2px solid var(--blue);
  outline-offset:3px;border-radius:6px}

/* ---- offre en preparation ---- */
.pop-bientot{background:#FBFBFC;border-style:dashed;border-color:#D8DEE8}
.pop-bientot .pop-oname,.pop-bientot .pop-price b{color:#59637A}
.pop-bientot li{color:#6C7588}.pop-bientot li svg{stroke:#9AA6B8}
.pop-bientot .pop-omodule img{opacity:.9}
.pop-badge-soon{background:#E7ECF3;color:#45526B;border:1px solid #D2DAE6}
.pop-onote{margin-top:16px;padding-top:13px;border-top:1px dashed var(--line);
  font-size:12.6px;line-height:1.5;color:#6C7588;text-align:center}

/* ---- composition centree ---- */
.pop-hero::after{right:auto;left:50%;transform:translateX(-50%);top:-380px;width:940px;height:940px}
.pop-hero .pop-wrap{text-align:center;padding:54px 28px 62px}
.pop-lockup{justify-content:center;gap:30px;padding-bottom:28px;margin-bottom:30px}
.pop-tag{order:-1;flex:0 0 100%;margin-left:0;text-align:center;margin-bottom:2px}
.pop-hero h1{max-width:19ch;margin-left:auto;margin-right:auto}
.pop-lede{max-width:60ch;margin-left:auto;margin-right:auto}
.pop-claim{max-width:56ch;margin:22px auto 0;padding:15px 22px;border-left:0;border-radius:14px;
  background:rgba(0,128,255,.10);border:1px solid rgba(95,179,255,.30);text-align:center}
.pop-actions{justify-content:center;margin-top:30px}
.pop-strip .pop-wrap{flex-direction:column;text-align:center;gap:18px}
.pop-strip-lead{flex:none;max-width:62ch}
.pop-marks{justify-content:center}
.pop-head{max-width:62ch;margin-left:auto;margin-right:auto;text-align:center}
.pop-pt{text-align:center}
.pop-ic{margin-left:auto;margin-right:auto}
.pop-fl{text-align:center}
.pop-flow-note{text-align:center}
.pop-oname,.pop-osub{text-align:center}
.pop-price{justify-content:center}
.pop-omodule img{margin-left:auto;margin-right:auto}
.pop-badge{left:50%;transform:translateX(-50%)}
.pop-confirm .pop-wrap{flex-direction:column;justify-content:center;text-align:center;gap:26px}
.pop-cb-txt{flex:none;max-width:70ch}
.pop-confirm p{margin-left:auto;margin-right:auto}
.pop-connect .pop-wrap{grid-template-columns:1fr;justify-items:center;text-align:center;gap:32px}
.pop-connect p{margin-left:auto;margin-right:auto}
.pop-sim{width:100%;max-width:520px;text-align:left}
.pop-foot .pop-wrap{justify-content:center;text-align:center;flex-direction:column;gap:14px}
.pop-op,.pop-links{justify-content:center}

/* ---- connexion : panneau ---- */
.pop-connect{position:relative;overflow:hidden}
.pop-connect::before{content:"";position:absolute;left:50%;top:34%;transform:translateX(-50%);
  width:860px;height:540px;border-radius:50%;pointer-events:none;
  background:radial-gradient(circle,rgba(0,128,255,.17) 0%,rgba(0,128,255,0) 66%)}
.pop-connect .pop-wrap{position:relative;z-index:1;gap:34px}
.pop-sim{max-width:660px;padding:0;overflow:hidden;border-radius:20px;
  background:linear-gradient(180deg,#17223C 0%,#101A2F 100%);
  border:1px solid rgba(255,255,255,.14);
  box-shadow:0 34px 72px -34px rgba(0,110,235,.55)}
.pop-sim-hd{margin:0;padding:15px 22px;background:rgba(255,255,255,.045);
  border-bottom:1px solid rgba(255,255,255,.09)}
.pop-sim-grid{gap:0;padding:4px 22px}
.pop-sim-grid>div{display:flex;align-items:baseline;justify-content:space-between;gap:20px;
  padding:14px 0;border-bottom:1px solid rgba(255,255,255,.07)}
.pop-sim-grid>div:last-child{border-bottom:0}
.pop-lab{flex:0 0 auto}
.pop-val{margin-top:0;text-align:right;font-size:14px}
.pop-sim-psc{margin:2px 22px 22px}
.pop-btn-ghostw{border-color:rgba(255,255,255,.55);color:#fff}
.pop-btn-ghostw:hover{border-color:#fff;color:#fff}
.pop-osm .pop-lede{margin-left:auto;margin-right:auto}
.pop-osm .pop-claim{margin:22px auto 0}
.pop-osm .pop-strip-lead{margin-left:auto;margin-right:auto}
.pop-osm .pop-claim{color:#fff;font-weight:600}

/* ---- quatre encarts ---- */
.pop-four{display:grid;grid-template-columns:repeat(2,1fr);gap:18px;align-items:stretch}
.pop-osm .pop-pt{display:flex;flex-direction:column;padding:28px 26px;text-align:center}
.pop-osm .pop-pt h3{font-size:18px;line-height:1.3;margin-bottom:10px}
.pop-osm .pop-pt p{font-size:14px;line-height:1.6;max-width:52ch;margin-left:auto;margin-right:auto}
.pop-osm .pop-head p{margin-top:12px;font-size:15.5px}
.pop-osm .pop-pt ul{margin-top:18px;padding-top:16px;border-top:1px solid var(--line);
  display:grid;gap:10px;text-align:left;flex:1;align-content:start}
.pop-osm .pop-pt li{display:flex;gap:10px;font-size:13.6px;line-height:1.5;color:#3D4759}
.pop-osm .pop-pt li svg{width:14px;height:14px;flex:0 0 14px;margin-top:4px;stroke:var(--ok);
  fill:none;stroke-width:2.6;stroke-linecap:round;stroke-linejoin:round}
.pop-seelink{display:inline-flex;align-items:center;gap:8px;margin-top:16px;align-self:center;
  font-size:13.6px;font-weight:600;color:var(--blue);text-decoration:none;
  border:1px solid var(--bline);background:var(--bsoft);border-radius:999px;padding:9px 17px}
.pop-seelink:hover{background:#DCEDFF;color:var(--blue)}
.pop-before{display:block;margin-top:15px;padding-top:12px;border-top:1px dashed var(--line);
  font-size:12.6px;color:#7C8698;font-style:italic;text-align:left}
@media (max-width:900px){.pop-four{grid-template-columns:1fr}}
/* ---- IDM Optic : la tuile telle qu'elle s'affiche ---- */
.pop-int{padding-bottom:0}
.pop-tuile{max-width:340px;margin:0 auto;background:var(--surface);border:1px solid var(--line);
  border-radius:16px;padding:18px 20px 20px;text-align:left;
  box-shadow:0 18px 40px -26px rgba(15,23,42,.45)}
.pop-tu-hd{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px}
.pop-tu-hd img{height:19px;width:auto;display:block}
.pop-tu-go{width:30px;height:30px;flex:0 0 30px;border-radius:50%;display:grid;place-items:center;
  background:var(--blue)}
.pop-tu-go svg{width:15px;height:15px;stroke:#fff;fill:none;stroke-width:2.2;stroke-linecap:round;
  stroke-linejoin:round}
.pop-tu-ct{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.pop-tu-ct>div{display:grid;grid-template-columns:auto 1fr;grid-template-rows:auto auto auto;
  column-gap:9px;align-items:center}
.pop-tu-svc{grid-row:1;grid-column:1 / -1;font-style:normal;font-size:9.4px;font-weight:700;
  letter-spacing:.09em;text-transform:uppercase;color:#8892A4;margin-bottom:7px;line-height:1.2}
.pop-tu-ct svg{grid-row:2;width:17px;height:17px;stroke:#E8663C;fill:none;stroke-width:1.8;
  stroke-linecap:round;stroke-linejoin:round}
.pop-osm .pop-tu-ct b{grid-row:2;font-size:26px;font-weight:600;color:#26304A;line-height:1;
  font-variant-numeric:tabular-nums}
.pop-osm .pop-tu-ct span{grid-row:3;grid-column:1 / -1;margin-top:5px;font-size:12px;
  color:var(--muted);line-height:1.3}
.pop-tu-next{display:flex;align-items:center;gap:8px;margin-top:16px;padding-top:13px;
  border-top:1px solid var(--line)}
.pop-tu-next svg{width:15px;height:15px;flex:0 0 15px;stroke:#9AA4B4;fill:none;stroke-width:1.8;
  stroke-linecap:round;stroke-linejoin:round}
.pop-osm .pop-tu-next b{font-size:13px;color:#26304A;font-variant-numeric:tabular-nums}
.pop-osm .pop-tu-next span{font-size:12.6px;color:var(--muted);line-height:1.3}
.pop-osm .pop-int-note{max-width:70ch;margin:26px auto 0;text-align:center;font-size:14px;
  line-height:1.65;color:#5A6577}
@media (max-width:420px){.pop-tu-ct{grid-template-columns:1fr;gap:12px}}
`;
